import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client';
import { Apollo, gql } from 'apollo-angular';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap, timeout, toArray } from 'rxjs/operators';
import { mapGraphQLEventToIEvent } from '../../graphql/events/events.mapping';
import { getEventsForStudentInTimeRangeQuery } from '../../graphql/events/events.queries';
import { Data, Event } from '../../graphql/models/gqlModels';
import { IEvent } from '../../models/event';

@Injectable({
    providedIn: 'root'
})
export class EventsService {

    constructor(
        private readonly apollo: Apollo,
    ) { }

    // Fetch events for multiple students within a date range (single query per student)
    getEventsForStudents(studentIds: string[], startDate: Date, endDate: Date): Observable<IEvent[]> {
        if (!studentIds?.length) {
            return of([]);
        }
        if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
            return of([]);
        }
        if (startDate > endDate) {
            // Swap if reversed
            const tmp = startDate;
            startDate = endDate;
            endDate = tmp;
        }

        return from(studentIds).pipe(
            mergeMap(studentId => this.getStudentEventsInRange(studentId, startDate, endDate)),
            toArray(),
            map(eventArrays => this.mergeEventsByStudentIds(eventArrays.flat()))
        );
    }

    private mergeEventsByStudentIds(events: IEvent[]): IEvent[] {
        const eventMap = new Map<string, IEvent>();
        events.forEach(event => {
            if (eventMap.has(event.id)) {
                const existingEvent = eventMap.get(event.id)!;
                const combinedStudentIds = [
                    ...(existingEvent.studentIds || []),
                    ...(event.studentIds || [])
                ];
                existingEvent.studentIds = [...new Set(combinedStudentIds)];
            } else {
                eventMap.set(event.id, { ...event });
            }
        });
        return Array.from(eventMap.values());
    }

    // Single time-range query per student
    private getStudentEventsInRange(studentId: string, startDate: Date, endDate: Date): Observable<IEvent[]> {
        const gqlString = getEventsForStudentInTimeRangeQuery(studentId, startDate, endDate);
        return this.apollo.query<Data>({
            query: gql`${gqlString}`
        }).pipe(
            timeout(5000),
            map(response => this.extractEventsFromResponse(response, studentId))
        );
    }

    private extractEventsFromResponse(response: ApolloQueryResult<Data>, studentId: string): IEvent[] {
        const content = response.data.eventSearch?.content;
        if (!Array.isArray(content) || !content.length) {
            return [];
        }
        return content.map((event: Event) => mapGraphQLEventToIEvent(event, studentId));
    }

    getEventsForStudent(studentId: string, startDate: Date, endDate: Date): Observable<IEvent[]> {
        return this.getEventsForStudents([studentId], startDate, endDate);
    }
}
